/* @src/pre-deployment/src/components/ComponentTestView.js v1.3 */
/* v1.3: Error pipeline overhaul - use SafeErrorBoundary so test bench errors go to RawErrorService → pipeline */
import React, { useState, Suspense, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { getComponentFromRegistry } from '../../utils/componentRegistry';
import ConsoleOverlay, { pushLog } from '../shared/ConsoleOverlay';
import SafeErrorBoundary from '../shared/SafeErrorBoundary';
import TeachModule from '../TeachModule/TeachModule';

import '../../styles/App.css'; // Reuse existing layout classes

const ComponentTestView = () => {
    // NOTE: Route param is :name, rename to componentName for clarity
    const { name: componentName, level, chapter } = useParams();
    const navigate = useNavigate();
    const { search } = useLocation();
    const { isAdmin } = useAuth();

    // Parse query params
    const queryParams = new URLSearchParams(search);
    const contentId = queryParams.get('contentId');
    const moduleKeyParam = queryParams.get('moduleKey'); // e.g. dialogueVocab for DialogueUniversal

    // Overlay Visibility States
    const [showLog, setShowLog] = useState(false);
    const [showData, setShowData] = useState(false);
    const [showTracking, setShowTracking] = useState(false);
    const [showTeachOverlay, setShowTeachOverlay] = useState(false);
    const [testKey, setTestKey] = useState(0);

    // ... (Navigation Mocks omitted for brevity, keep unchanged) ...
    // Note: I will only replace the top block and the return block to insert contentId

    const goNext = useCallback(() => {
        pushLog('Navigation: Next clicked', 'info', 'system');
        alert('Component requested "Next" navigation');
    }, []);

    const goPrevious = useCallback(() => {
        pushLog('Navigation: Previous clicked', 'info', 'system');
        navigate(-1);
    }, [navigate]);

    const goMenu = useCallback(() => {
        pushLog('Navigation: Home clicked', 'info', 'system');
        navigate('/app-homepage');
    }, [navigate]);

    const navigation = useMemo(() => ({
        next: goNext,
        previous: goPrevious,
        menu: goMenu
    }), [goNext, goPrevious, goMenu]);

    // Teach overlay handlers
    const handleOpenTeach = useCallback(() => {
        pushLog('Opening Teach overlay...', 'info', 'system');
        setShowTeachOverlay(true);
    }, []);

    const handleCloseTeach = useCallback(() => {
        pushLog('Teach overlay closed - continuing with component', 'success', 'system');
        setShowTeachOverlay(false);
    }, []);

    // Navigation for TeachModule when in overlay mode
    const teachNavigation = useMemo(() => ({
        next: () => {
            pushLog('Teach complete - closing overlay', 'success', 'system');
            setShowTeachOverlay(false);
        },
        previous: () => {
            pushLog('Teach overlay closed via back', 'info', 'system');
            setShowTeachOverlay(false);
        },
        menu: () => {
            pushLog('Teach overlay closed via menu', 'info', 'system');
            setShowTeachOverlay(false);
        }
    }), []);

    // Auto-test launcher (configured per module mock)
    const runAutoTest = () => {
        pushLog(`Starting automated test for ${componentName}`, 'warn', 'system');
        // Implement specific logic here if needed or just log events
        setTimeout(() => pushLog('Auto-test: Sequence 1 completed', 'success', 'system'), 1000);
        setTimeout(() => pushLog('Auto-test: Checkpoint 2 passed', 'success', 'system'), 2000);
        setTimeout(() => pushLog('Auto-test: Verification successful', 'info', 'system'), 3000);
    };

    if (!isAdmin) {
        return <div style={{ padding: 40, background: '#1a1a2e', color: '#fff', minHeight: '100vh' }}>Access Denied</div>;
    }

    const Component = getComponentFromRegistry(componentName);

    return (
        <div className="chapter-container" style={{ background: '#f5f7fb' }}>
            {/* Test Audit Header - keep custom test bench colors */}
            <div className="exercise-top-navigation" style={{ background: '#16213e', borderBottom: '2px solid #00d2ff' }}>
                <div className="module-info">
                    <span style={{ color: '#00d2ff', fontWeight: 'bold' }}>TEST BENCH</span>
                    <span className="separator">•</span>
                    <span>{componentName}</span>
                    <span className="separator">•</span>
                    <span>L{level} C{chapter}</span>
                    {contentId && (
                        <>
                            <span className="separator">•</span>
                            <span style={{ color: '#F59E0B' }}>ID: {contentId}</span>
                        </>
                    )}
                </div>

                <div className="module-nav-buttons">
                    <button className={`module-nav-btn secondary ${showLog ? 'active' : ''}`} onClick={() => setShowLog(!showLog)}>
                        <i className="fas fa-terminal"></i> Log
                    </button>
                    <button className={`module-nav-btn secondary ${showData ? 'active' : ''}`} onClick={() => setShowData(!showData)}>
                        <i className="fas fa-database"></i> Data
                    </button>
                    <button className={`module-nav-btn secondary ${showTracking ? 'active' : ''}`} onClick={() => setShowTracking(!showTracking)}>
                        <i className="fas fa-chart-line"></i> Tracking
                    </button>
                    <span className="separator">|</span>

                    {/* NEW: Teach Overlay Button */}
                    <button
                        className={`module-nav-btn ${showTeachOverlay ? 'active' : ''}`}
                        onClick={handleOpenTeach}
                        style={{ background: showTeachOverlay ? '#4F46E5' : 'linear-gradient(90deg, #4F46E5, #06B6D4)' }}
                        title="Open Teach Module Overlay"
                    >
                        <i className="fas fa-graduation-cap"></i> Teach
                    </button>

                    <span className="separator">|</span>
                    <button className="module-nav-btn secondary" onClick={() => setTestKey(prev => prev + 1)}>
                        <i className="fas fa-redo"></i> Restart
                    </button>
                    <button className="module-nav-btn" onClick={runAutoTest} style={{ background: 'linear-gradient(90deg, #ff8c00, #ff4500)' }}>
                        <i className="fas fa-vial"></i> Auto Test
                    </button>
                    <span className="separator">|</span>
                    <button className="module-nav-btn secondary" onClick={goMenu}>
                        <i className="fas fa-home"></i> Home
                    </button>
                </div>
            </div>

            {/* Component Area - CSS Grid handles layout; SafeErrorBoundary feeds errors to RawErrorService */}
            <div className="module-content">
                <SafeErrorBoundary key={`${testKey}-${componentName}`} componentName={componentName} level={level} chapter={chapter}>
                    <Suspense fallback={<div className="module-loading-placeholder">Initializing Test Context...</div>}>
                        {Component ? (
                            <Component
                                key={testKey}
                                level={level}
                                chapter={chapter}
                                contentDocId={contentId}
                                moduleKey={componentName === 'DialogueUniversal' ? (moduleKeyParam || 'dialogueComp') : `test-${componentName}`}
                                moduleConfig={componentName === 'DialogueUniversal' ? { displayName: `Test: ${componentName}`, contentDocId: contentId } : { displayName: `Test: ${componentName}` }}
                                moduleName={componentName === 'DialogueUniversal' ? (moduleKeyParam || 'dialogueComp') : undefined}
                                navigation={navigation}
                                isTestEnvironment={true}
                            />
                        ) : (
                            <div style={{ padding: 40, textAlign: 'center' }}>
                                <h3>Component Not Found</h3>
                                <p>Registry name "<b>{componentName}</b>" could not be resolved.</p>
                            </div>
                        )}
                    </Suspense>
                </SafeErrorBoundary>
            </div>

            {/* Console Overlays */}
            <ConsoleOverlay isVisible={showLog} title="Runtime Execution Log" type="system" onClose={() => setShowLog(false)} />
            <ConsoleOverlay isVisible={showData} title="Data Service Audit" type="data" onClose={() => setShowData(false)} />
            <ConsoleOverlay isVisible={showTracking} title="Tracking Event Stream" type="tracking" onClose={() => setShowTracking(false)} />

            {/* Teach Module Overlay - SafeErrorBoundary feeds errors to RawErrorService */}
            {showTeachOverlay && (
                <SafeErrorBoundary componentName="TeachModule" level={level} chapter={chapter}>
                    <TeachModule
                        level={level}
                        chapter={chapter}
                        contentId={contentId}
                        isOverlay={true}
                        onClose={handleCloseTeach}
                        navigation={teachNavigation}
                        isTestEnvironment={true}
                    />
                </SafeErrorBoundary>
            )}

            {/* Styles for active state */}
            <style>{`
        .module-nav-btn.active {
          background: #00d2ff !important;
          color: #000 !important;
        }
      `}</style>
        </div>
    );
};

export default ComponentTestView;
